import { useEffect, useMemo, useRef } from 'react';
import { usePathname } from 'expo-router';
import { View, Text } from 'react-native';
import { Button } from './Button';
import {
  reportUiError,
  sendClientObservability,
  serializeClientError,
} from '../lib/observability';

type ErrorStateMetadata = Record<string, string | number | boolean | null>;

interface ErrorStateProps {
  message?: string;
  onRetry?: () => void;
  retryLabel?: string;
  error?: unknown;
  screen?: string;
  metadata?: ErrorStateMetadata;
}

export function ErrorState({
  message = 'Something went wrong.',
  onRetry,
  retryLabel = 'Try Again',
  error,
  screen,
  metadata,
}: ErrorStateProps) {
  const pathname = usePathname();
  const reportedKeyRef = useRef<string | null>(null);
  const serializedError = useMemo(() => serializeClientError(error), [error]);
  const expectedMissingWorkspace =
    metadata?.householdMissing === true && message === "We couldn't find your workspace.";

  useEffect(() => {
    const reportKey = JSON.stringify({
      route: pathname,
      screen,
      message,
      fingerprint: serializedError.fingerprint ?? null,
    });

    if (reportedKeyRef.current === reportKey) {
      return;
    }

    reportedKeyRef.current = reportKey;

    if (!expectedMissingWorkspace) {
      reportUiError({
        error,
        message,
        route: pathname,
        screen,
        component: 'ErrorState',
        metadata,
      });
    }

    void sendClientObservability({
      kind: expectedMissingWorkspace ? 'event' : 'error',
      eventName: expectedMissingWorkspace
        ? 'ui.expected_missing_workspace'
        : 'ui.error_state_rendered',
      route: pathname,
      screen,
      component: 'ErrorState',
      message,
      metadata,
      ...serializedError,
    });
  }, [expectedMissingWorkspace, message, metadata, pathname, screen, serializedError]);

  return (
    <View className="flex-1 items-center justify-center p-8">
      <Text className="text-4xl mb-4">⚠️</Text>
      <Text className="text-base text-gray-700 dark:text-gray-100 text-center mb-6">{message}</Text>
      {onRetry && (
        <Button onPress={onRetry} variant="secondary" size="sm">
          {retryLabel}
        </Button>
      )}
    </View>
  );
}
